import { Nullable } from '@/utils';

export enum BillingPeriod {
	monthly = 'monthly',
	yearly = 'yearly'
}

export interface Plan {
	id: string;
	active: boolean;
	amount?: number;
	object: string;
	created: number;
	product: string;
	currency: string;
	interval: string;
	livemode: boolean;
	metadata: any[];
	nickname?: any;
	tiers_mode: string;
	usage_type: string;
	amount_decimal: string;
	billing_scheme: string;
	interval_count: number;
	aggregate_usage?: any;
	transform_usage?: any;
	trial_period_days?: any;
}

export interface Recurring {
	interval: string;
	usage_type: string;
	interval_count: number;
	aggregate_usage?: any;
	trial_period_days?: any;
}

export interface Price {
	id: string;
	type: string;
	active: boolean;
	object: string;
	created: number;
	product: string;
	currency: string;
	livemode: boolean;
	metadata: any[];
	nickname?: any;
	recurring: Recurring;
	lookup_key?: any;
	tiers_mode: string;
	unit_amount?: number;
	tax_behavior: string;
	billing_scheme: string;
	custom_unit_amount?: any;
	transform_quantity?: any;
	unit_amount_decimal: string;
}

export interface Period {
	end: number;
	start: number;
}

export interface Metadata {
	name: string;
}

export interface ProrationDetails {
	credited_items?: any;
}

export interface Line {
	id: string;
	plan: Plan;
	type: string;
	price: Price;
	amount: number;
	object: string;
	period: Period;
	currency: string;
	livemode: boolean;
	metadata: Metadata;
	quantity: number;
	discounts: any[];
	proration: boolean;
	tax_rates: any[];
	description: string;
	tax_amounts: any[];
	discountable: boolean;
	subscription: string;
	discount_amounts: any[];
	proration_details: ProrationDetails;
	subscription_item: string;
	amount_excluding_tax: number;
	unit_amount_excluding_tax: string;
}

export interface UpcomingInvoice {
	total: string;
	status: string;
	lines: Line[];
	created_at: Date;
	updated_at: Date;
}

export enum SubscriptionName {
	phoneSystem = 'phone-system',
	faxSystem = 'fax-system',
	teamSeats = 'team-seats',
	virtualMailSystem = 'virtual-mail-system'
}

export enum PhoneSystemItemName {
	phoneNumbers = 'Phone Numbers',
	phoneMinutes = 'Phone Minutes'
}

export enum TeamSeatsItemName {
	teamSeats = 'team-seats',
	itemName = 'Team Seats'
}

export enum FaxSystemItemName {
	faxNumbers = 'Fax Numbers'
}

export enum ProductAlias {
	phoneNumbers = 'phone-numbers',
	teamSeats = 'team-seats',
	faxNumbers = 'fax-numbers',
	virtualMailSystem = 'virtual-mails'
}

export interface StripePaymentMethod {
	id: number;
	stripe_payment_id: string;
	country: string;
	brand: string;
	funding: string;
	exp_month: string;
	exp_year: string;
	last4: string;
	is_primary: boolean;
}

export interface StripeSubscriptionItem {
	id: number;
	stripe_id: string;
	stripe_product: string;
	stripe_price: string;
	quantity: number;
	created_at: string;
	updated_at: string;
	price: string;
	interval: string;
	friendlyName:
		| PhoneSystemItemName
		| FaxSystemItemName
		| TeamSeatsItemName;
	subscriptionName: string;
	productAlias: ProductAlias;
}

export interface StripeInvoice {
	invoiceNumber: string;
	total: string;
	status: string;
	hostedInvoiceUrl: string;
	invoicePdf: string;
	created_at: string;
	updated_at: string;
	subscription_id: number;
}

export enum SubscriptionStatus {
	active = 'active',
	inactive = 'inactive',
	incomplete = 'incomplete',
	pasDue = 'past_due',
	canceled = 'canceled'
}

export interface StripeSubscription {
	id: number;
	name: SubscriptionName;
	stripe_status: SubscriptionStatus;
	trial_ends_at: null;
	ends_at: Nullable<string>;
	created_at: string;
	updated_at: string;
	items: StripeSubscriptionItem[];
	invoices: StripeInvoice[];
	upcomingInvoice: UpcomingInvoice;
}

export interface AvailablePlanItem {
	subscription_name: SubscriptionName;
	stripe_product_name:
		| PhoneSystemItemName
		| FaxSystemItemName;
	stripe_product_id: string;
	stripe_price_id: string;
	stripe_price: string;
	interval: string;
}
